import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, FileUp, Loader2 } from "lucide-react";
import {
  DEVIS_SIGNE_ACCEPT,
  DEVIS_SIGNE_MAX_MB,
  uploadDevisSigne,
  validateDevisSigneFile,
} from "../../lib/devisSigneUpload";
import { SignedQuoteViewer } from "./SignedQuoteViewer";

interface SignedQuoteUploadProps {
  jobId: string;
  /** Chemins Storage déjà enregistrés (jobs.signed_quote_url text[]) */
  existingPaths?: string[] | null;
  /** Liste complète après ajout(s) ou suppression. */
  onChange: (paths: string[]) => void | Promise<void>;
  canDelete?: boolean;
  disabled?: boolean;
  /** Affiche l’astérisque « obligatoire » (ex. espace commercial). */
  required?: boolean;
}

export function SignedQuoteUpload({
  jobId,
  existingPaths,
  onChange,
  canDelete = true,
  disabled = false,
  required = false,
}: SignedQuoteUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const paths = (existingPaths ?? []).map((p) => p.trim()).filter(Boolean);

  const handleFiles = async (fileList: FileList | null | undefined) => {
    if (!fileList?.length || disabled || uploading) return;
    setError(null);
    setSuccess(false);

    const files = Array.from(fileList);
    for (const file of files) {
      const validation = validateDevisSigneFile(file);
      if (!validation.ok) {
        setError(validation.message);
        return;
      }
    }

    setUploading(true);
    const uploaded: string[] = [];
    try {
      for (const file of files) {
        const { path, error: uploadError } = await uploadDevisSigne(jobId, file);
        if (uploadError || !path) {
          setError(uploadError ?? "Échec de l'envoi du fichier.");
          if (uploaded.length) {
            await onChange([...paths, ...uploaded]);
          }
          return;
        }
        uploaded.push(path);
      }
      await onChange([...paths, ...uploaded]);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'envoi du fichier.");
      if (uploaded.length) {
        try {
          await onChange([...paths, ...uploaded]);
        } catch {
          /* ignore secondary persist error */
        }
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemoved = async (storagePath: string) => {
    await onChange(paths.filter((p) => p !== storagePath));
  };

  return (
    <div className="space-y-3">
      {paths.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("signedQuote.title")}{" "}
            <span className="font-normal text-gray-500 dark:text-slate-400">
              ({paths.length})
            </span>
          </p>
          {paths.map((path) => (
            <SignedQuoteViewer
              key={path}
              jobId={jobId}
              storagePath={path}
              canDelete={canDelete}
              onRemoved={handleRemoved}
              disabled={disabled || uploading}
            />
          ))}
        </div>
      )}

      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
        {paths.length === 0 ? t("signedQuote.title") : t("signedQuote.addMore")}{" "}
        {required ? <span className="text-red-500">*</span> : null}
      </label>
      <p className="text-xs text-gray-500 dark:text-slate-400">
        {t("signedQuote.fileTypesHint", { maxMb: DEVIS_SIGNE_MAX_MB })}
      </p>

      <div
        role="button"
        tabIndex={disabled || uploading ? -1 : 0}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled && !uploading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={`relative flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
          disabled || uploading
            ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 dark:border-white/10 dark:bg-slate-800/50"
            : "cursor-pointer border-blue-300 bg-blue-50/50 hover:border-blue-400 hover:bg-blue-50 dark:border-blue-500/40 dark:bg-blue-500/10 dark:hover:bg-blue-500/15"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={DEVIS_SIGNE_ACCEPT}
          multiple
          capture="environment"
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => void handleFiles(e.target.files)}
        />
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" aria-hidden />
        ) : (
          <FileUp className="h-8 w-8 text-blue-600 dark:text-blue-400" aria-hidden />
        )}
        <span className="text-sm font-medium text-gray-800 dark:text-slate-200">
          {uploading ? t("signedQuote.uploading") : t("signedQuote.tapToUpload")}
        </span>
        {paths.length > 0 && !uploading && (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">
            {t("signedQuote.addMoreHint")}
          </span>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {success && !error && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          {t("signedQuote.uploadSuccess")}
        </div>
      )}
    </div>
  );
}
